import { useMember } from '~/stores/useMember';
import { useLoadingBar } from '~/stores/useLoadingBar';
import { getAnonId, getSessionId } from '~/lib/identity';

// Default to same-origin relative URLs: in production (and the local
// single-service build) the app is served by this same server, so `/hotel/rooms`
// resolves against the page origin on any port. Set EXPO_PUBLIC_API_URL only
// when the app runs on a separate origin from the API (a standalone Expo dev
// server), per .env.example.
const API_URL = process.env.EXPO_PUBLIC_API_URL ?? '';

type ClientOptions = {
    method?: string;
    body?: string;
    showLoadingBar?: boolean;
};

// Paths that represent page-to-page navigation and primary page data loading.
// The top loading progress bar will only show for these requests (and never
// for autocomplete, background polling, or minor in-page actions).
const PAGE_LEVEL_PATHS = new Set([
    '/search/hotels',
    '/hotel/rooms',
    '/booking/prebook',
    '/payment/initiate',
    '/order/info',
    '/admin/overview',
]);

async function client<T>(path: string, options: ClientOptions = {}): Promise<T> {
    // Read directly from the store (outside React) rather than requiring every
    // call site to pass it in — booking/order endpoints need it, search/hotel
    // don't, and the token can change between requests (sign in/out).
    const token = useMember.getState().token;
    const headers: Record<string, string> = {
        // Sent on every request, signed in or not, so server-side activity can
        // be grouped per traveller and per visit.
        'X-SS-Anon-Id': getAnonId(),
        'X-SS-Session-Id': getSessionId()
    };

    // Only advertise a JSON body when there is one — a POST with an empty
    // body + application/json trips Fastify's empty-body guard (order/info
    // takes no body).
    if (options.body) {
        headers['Content-Type'] = 'application/json';
    }
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    // Bracket the request so the TopNav loading bar runs ONLY for page-to-page
    // requests (and never for autocomplete, polling, or inline actions).
    const cleanPath = path.split('?')[0];
    const showBar = options.showLoadingBar !== undefined
        ? options.showLoadingBar
        : PAGE_LEVEL_PATHS.has(cleanPath);

    const loadingBar = useLoadingBar.getState();
    if (showBar) {
        loadingBar.begin();
    }
    try {
        const response = await fetch(`${API_URL}${path}`, {
            method: options.method ?? 'GET',
            headers,
            body: options.body
        });

        // A 401 on a request that carried our token means the session expired
        // or was revoked server-side. Drop it so the UI stops claiming the
        // traveller is signed in (and offers sign-in) instead of failing
        // every member-only call in silence.
        if (response.status === 401 && token && useMember.getState().token === token) {
            useMember.getState().signOut();
        }

        return await response.json() as T;
    } finally {
        if (showBar) {
            loadingBar.end();
        }
    }
}

// Failed calls answer `{ error: 'code', message: 'Customer-facing text' }`.
// Show the message; fall back to the code, then to the caller's own text.
function errorMessage(response: unknown, fallback: string): string {
    const body = (response ?? {}) as { error?: unknown; message?: unknown };
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
    return fallback;
}

export { API_URL, client, errorMessage };
